import React, { useState } from 'react';
import { Box, Button, Switch, TextField, Typography } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import toast from 'react-hot-toast';
import { PARTNER_BRAND, PARTNER_SWITCH_SX } from '../../partnerDashboardTheme';
import RequireJoinApprovalField from '../../../shared/registration/RequireJoinApprovalField';
import AttendeeEligibilityField from '../../../shared/registration/AttendeeEligibilityField';
import {
  COMMUNITY_DESCRIPTION_MAX,
  createCommunityEditInitialState,
} from '../communityEditConstants';
import {
  companyInfoOutlinedButtonSx,
  companyInfoPrimaryButtonSx,
  COMPANY_INFO_FIELD_SX,
  companyInfoLabelSx,
} from '../../../PartnerSettings/PartnerSettingsSection/companyInfoStyles';

const EditCommunityPanel = ({ channel, onBack, onSave, saving = false }) => {
  const [form, setForm] = useState(() => createCommunityEditInitialState(channel));

  const updateField = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleDescriptionChange = (event) => {
    const nextValue = event.target.value.slice(0, COMMUNITY_DESCRIPTION_MAX);
    setForm((prev) => ({ ...prev, description: nextValue }));
  };

  const handleSave = () => {
    if (!form.title.trim()) {
      toast.error('Community title is required');
      return;
    }

    onSave?.(form);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIosIcon sx={{ fontSize: 14 }} />}
          onClick={onBack}
          sx={{
            color: PARTNER_BRAND,
            textTransform: 'none',
            fontWeight: 500,
            p: 0,
            minWidth: 'auto',
            '&:hover': { bgcolor: 'transparent', opacity: 0.85 },
            '& .MuiButton-startIcon': { mr: -0.25, ml: -0.5 },
          }}
        >
          Back
        </Button>
        <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: 'text.primary' }}>
          Edit Community
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, flex: 1 }}>
        <Box>
          <Typography sx={companyInfoLabelSx}>Community Title</Typography>
          <TextField
            fullWidth
            value={form.title}
            onChange={(event) => updateField('title')(event.target.value)}
            sx={COMPANY_INFO_FIELD_SX}
          />
        </Box>

        <Box>
          <Typography sx={companyInfoLabelSx}>Description</Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={form.description}
            onChange={handleDescriptionChange}
            sx={COMPANY_INFO_FIELD_SX}
          />
          <Typography
            component="span"
            sx={{
              display: 'block',
              textAlign: 'right',
              mt: 0.5,
              fontSize: '0.75rem',
              color: 'text.disabled',
            }}
          >
            {form.description.length}/{COMMUNITY_DESCRIPTION_MAX}
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ ...companyInfoLabelSx, fontSize: '1rem', mb: 1.5 }}>
            Membership Settings
          </Typography>

          <RequireJoinApprovalField
            variant="community"
            checked={form.requireJoinApproval}
            onChange={updateField('requireJoinApproval')}
            SwitchComponent={Switch}
            switchSx={PARTNER_SWITCH_SX}
            bordered={false}
          />

          <AttendeeEligibilityField
            variant="community"
            value={form.attendeeEligibility}
            onChange={updateField('attendeeEligibility')}
            radioRow={false}
            showInfoAlert={false}
            bordered={false}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mt: 4,
          pt: 3,
          borderTop: '1px solid',
          borderTopColor: 'divider',
        }}
      >
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={saving}
          sx={companyInfoOutlinedButtonSx}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSave}
          disabled={saving}
          sx={companyInfoPrimaryButtonSx}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};

export default EditCommunityPanel;
